import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import type {
  GridTableType,
  TableBodyType,
  TableCaptionType,
  TableCellType,
  TableColumnType,
  TableFooterType,
  TableHeadType,
  TableRowType,
} from '../Table';
import { Table, TableBody, TableCaption, TableCell, TableColumn, TableFooter, TableHead, TableRow } from '../Table';

import styles from './styles/GridTable.module.scss';

const GridTable = ({ children, className, label, mobileColumns, ...props }: GridTableType) => {
  const tableRef = useRef<HTMLTableElement>(null);

  const [tableBodyCount, setTableBodyCount] = useState(1);

  useEffect(() => {
    if (tableRef.current) {
      const tableBodies = tableRef.current.getElementsByClassName(styles.gridTableBody);

      setTableBodyCount(tableBodies.length);
    }
  }, []);

  return (
    <div
      aria-label={label}
      className={styles.gridTableWrapper}
      data-cxy-name="grid-table"
      // No caller `style` merged in here, deliberately. `{...props}` is spread on the inner
      // `<Table>` below, not on this wrapper, so a caller's `style` reaches the `<table>` element and
      // never collided with this grid positioning. Destructuring it to "merge" it here silently
      // RELOCATED it from the table to the wrapper — a different element, different layout.
      style={{
        '--gridWidth': tableBodyCount + 1,
        ...(tableBodyCount === 1 ? { gridTemplateColumns: 'auto' } : {}),
        '--mobileColumns': mobileColumns,
      }}
    >
      <Table
        className={clsx(styles.gridTable, className, {
          [styles.hasMultipleTableBodies]: tableBodyCount > 1,
        })}
        ref={tableRef}
        {...props}
      >
        {children}
      </Table>
    </div>
  );
};

const GridTableRow = ({ children, className, ...props }: TableRowType) => (
  <TableRow className={clsx(styles.gridTableRow, className)} {...props}>
    {children}
  </TableRow>
);

const GridTableColumn = ({ children, className, isMain, ...props }: { isMain?: boolean } & TableColumnType) => (
  <TableColumn
    className={clsx(
      styles.gridTableColumn,
      {
        [styles.mainColumn]: isMain,
      },
      className,
    )}
    {...props}
  >
    {children}
  </TableColumn>
);

const GridTableCell = ({
  children,
  className,
  isFullWidthMobile,
  isMain,
  order,
  start,
  ...props
}: {
  isFullWidthMobile?: boolean;
  isMain?: boolean;
  order?: number;
  start?: number;
} & TableCellType) => (
  <TableCell
    className={clsx(
      styles.gridTableCell,
      {
        [styles.fullWidthMobile]: isFullWidthMobile,
        [styles.mainColumn]: isMain,
      },
      className,
    )}
    style={{ '--cellOrder': order, '--start': start }}
    {...props}
  >
    {children}
  </TableCell>
);

const GridTableFooter = ({ children, className, ...props }: TableFooterType) => (
  <TableFooter {...props} className={clsx(styles.gridTableFooter, className)}>
    {children}
  </TableFooter>
);

const GridTableHead = ({ children, className, ...props }: TableHeadType) => (
  <TableHead {...props} className={clsx(styles.gridTableHeader, className)}>
    {children}
  </TableHead>
);

const GridTableBody = ({ children, className, ...props }: TableBodyType) => (
  <TableBody className={clsx(styles.gridTableBody, className)} {...props}>
    {children}
  </TableBody>
);

const GridTableCaption = ({ children, className, ...props }: TableCaptionType) => (
  <TableCaption className={clsx(styles.gridTableCaption, className)} {...props}>
    {children}
  </TableCaption>
);

GridTable.Caption = GridTableCaption;
GridTable.Row = GridTableRow;
GridTable.Head = GridTableHead;
GridTable.Body = GridTableBody;
GridTable.Column = GridTableColumn;
GridTable.Cell = GridTableCell;
GridTable.Footer = GridTableFooter;

export { GridTable };
